// Authentication utilities for session management
import axios from 'axios';
import { API_URL } from '../config';

// Token management
export const getToken = () => {
  return localStorage.getItem('token');
};

export const setToken = (token) => {
  localStorage.setItem('token', token);
};

export const getRefreshToken = () => {
  return localStorage.getItem('refreshToken');
};

export const setRefreshToken = (refreshToken) => {
  localStorage.setItem('refreshToken', refreshToken);
};

export const removeToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
};

// Check if token is expired (basic check without decoding)
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Basic JWT payload check (without verification)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true; // If we can't parse it, consider it expired
  }
};

// Check if token expires soon (within specified minutes)
export const isTokenExpiringSoon = (token, minutes = 10) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const expirationTime = payload.exp;
    const timeUntilExpiration = expirationTime - currentTime;
    return timeUntilExpiration < (minutes * 60); // Less than X minutes remaining
  } catch (error) {
    console.error('Error checking token expiration time:', error);
    return true;
  }
};

// Validate token with server
export const validateToken = async (token) => {
  if (!token) return false;
  
  try {
    const response = await axios.get(`${API_URL}/api/user`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.status === 200;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
};

// Refresh token using refresh token
export const refreshToken = async () => {
  const refreshTokenValue = getRefreshToken();
  
  if (!refreshTokenValue) {
    console.log('No refresh token available');
    return null;
  }

  try {
    const response = await axios.post(`${API_URL}/api/auth/refresh`, {
      refreshToken: refreshTokenValue
    });
    
    if (response.data.token && response.data.refreshToken) {
      setToken(response.data.token);
      setRefreshToken(response.data.refreshToken);
      console.log('Token refreshed successfully');
      return response.data.token;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // If refresh fails, clear tokens and logout
    removeToken();
    return null;
  }
};

// Handle authentication errors
export const handleAuthError = (error, onLogout) => {
  if (error.response?.status === 401) {
    console.log('Authentication failed - redirecting to login');
    removeToken();
    if (onLogout) {
      onLogout();
    } else {
      window.location.href = '/login';
    }
    return true;
  }
  return false;
};

// Create authenticated axios instance with automatic token refresh
export const createAuthenticatedAxios = (onLogout) => {
  const instance = axios.create({
    baseURL: API_URL,
  });

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // Request interceptor: Add token and proactively refresh if expiring soon
  instance.interceptors.request.use(
    async (config) => {
      let token = getToken();
      
      // Proactive refresh: If token expires in <10 minutes, refresh it
      if (token && isTokenExpiringSoon(token, 10)) {
        console.log('Token expiring soon, refreshing proactively...');
        const newToken = await refreshToken();
        if (newToken) {
          token = newToken;
        } else {
          // Refresh failed, but continue with current token (will retry on 401)
          console.log('Proactive refresh failed, will retry on 401');
        }
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle 401 errors with reactive token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If error is 401 and we haven't tried to refresh yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          
          if (newToken) {
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            // Refresh failed, logout user
            processQueue(new Error('Token refresh failed'), null);
            removeToken();
            if (onLogout) {
              onLogout();
            } else {
              window.location.href = '/login';
            }
            return Promise.reject(new Error('Authentication required'));
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          removeToken();
          if (onLogout) {
            onLogout();
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Session management
export class SessionManager {
  constructor() {
    this.checkInterval = null;
    this.onLogout = null;
    this.isChecking = false;
  }

  setLogoutCallback(callback) {
    this.onLogout = callback;
  }

  startSessionCheck() {
    // Check session every 5 minutes
    this.checkInterval = setInterval(() => {
      this.checkSession();
    }, 5 * 60 * 1000);
  }

  stopSessionCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  async checkSession() {
    if (this.isChecking) return;
    
    this.isChecking = true;
    const token = getToken();
    const refreshTokenValue = getRefreshToken();
    
    if (!token && !refreshTokenValue) {
      this.handleLogout();
      return;
    }

    // If access token is expired but we have refresh token, try to refresh
    if (isTokenExpired(token) && refreshTokenValue) {
      console.log('Access token expired, attempting refresh...');
      const newToken = await refreshToken();
      if (newToken) {
        console.log('Session refreshed successfully');
        this.isChecking = false;
        return;
      } else {
        console.log('Token refresh failed');
        this.handleLogout();
        return;
      }
    }

    // If access token is expired and no refresh token, logout
    if (isTokenExpired(token) && !refreshTokenValue) {
      console.log('Token expired and no refresh token available');
      this.handleLogout();
      return;
    }

    // If token is expiring soon, refresh proactively
    if (token && isTokenExpiringSoon(token, 10) && refreshTokenValue) {
      console.log('Token expiring soon, refreshing proactively...');
      await refreshToken();
    }

    // Validate with server (only if we have a valid token)
    if (token && !isTokenExpired(token)) {
      const isValid = await validateToken(token);
      if (!isValid) {
        console.log('Token invalid on server');
        // Try refresh before giving up
        if (refreshTokenValue) {
          const newToken = await refreshToken();
          if (!newToken) {
            this.handleLogout();
            return;
          }
        } else {
          this.handleLogout();
          return;
        }
      }
    }

    this.isChecking = false;
  }

  handleLogout() {
    this.isChecking = false;
    removeToken();
    
    if (this.onLogout) {
      this.onLogout();
    } else {
      // Show a gentle message before redirecting
      if (confirm('Your session has expired. Would you like to log in again?')) {
        window.location.href = '/login';
      } else {
        window.location.href = '/';
      }
    }
  }

  // Manual session check (call this before important operations)
  async ensureValidSession() {
    const token = getToken();
    const refreshTokenValue = getRefreshToken();
    
    // If no tokens at all, logout
    if (!token && !refreshTokenValue) {
      this.handleLogout();
      return false;
    }

    // If access token expired, try refresh
    if (isTokenExpired(token) && refreshTokenValue) {
      const newToken = await refreshToken();
      if (newToken) {
        return true;
      } else {
        this.handleLogout();
        return false;
      }
    }

    // If access token expired and no refresh token, logout
    if (isTokenExpired(token) && !refreshTokenValue) {
      this.handleLogout();
      return false;
    }

    // Validate with server
    if (token) {
      const isValid = await validateToken(token);
      if (!isValid) {
        // Try refresh before giving up
        if (refreshTokenValue) {
          const newToken = await refreshToken();
          if (newToken) {
            return true;
          }
        }
        this.handleLogout();
        return false;
      }
    }

    return true;
  }
}

// Global session manager instance
export const sessionManager = new SessionManager();


